import * as THREE from 'three';

export interface FleetDrone {
  id: string;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  target: THREE.Vector3;
  workState: 'idle' | 'moving' | 'working' | 'returning';
  workProgress: number;
  lastUpdate: number;
}

export interface FleetConfig {
  maxDrones: number;
  droneSpeed: number;
  workRange: number;
  efficiency: number; // 0-1, affects construction speed
  color: THREE.Color;
  laserColor: THREE.Color;
}

export interface WorkAnimation {
  particleEffects: boolean;
  laserBeams: boolean;
  progressIndicators: boolean;
  sparkIntensity: number;
}

export class ConstructionFleet extends THREE.Group {
  public poolName: string;
  public poolHashrate: number;
  public config: FleetConfig;
  
  // Fleet state
  private drones: Map<string, FleetDrone> = new Map();
  private targetSegment: number = -1;
  private targetPosition: THREE.Vector3 = new THREE.Vector3();
  private basePosition: THREE.Vector3 = new THREE.Vector3();
  
  // Visual components
  private dronesMesh: THREE.InstancedMesh;
  private laserSystem: THREE.LineSegments;
  private particleSystem: THREE.Points;
  private workAnimation: WorkAnimation;
  
  // Materials
  private droneMaterial: THREE.MeshLambertMaterial;
  private laserMaterial: THREE.LineBasicMaterial;
  private particleMaterial: THREE.PointsMaterial;
  
  // Animation state
  private time: number = 0;
  private activeWork: boolean = false;
  
  constructor(
    poolName: string,
    poolHashrate: number,
    config?: Partial<FleetConfig>
  ) {
    super();
    
    this.poolName = poolName;
    this.poolHashrate = poolHashrate;
    
    this.config = {
      maxDrones: Math.min(Math.max(Math.floor(poolHashrate / 10), 1), 50),
      droneSpeed: 2.0,
      workRange: 3.0,
      efficiency: 0.7 + Math.random() * 0.3, // Random efficiency per pool
      color: this.generatePoolColor(poolName),
      laserColor: new THREE.Color(0x00aaff),
      ...config,
    };
    
    this.workAnimation = {
      particleEffects: true,
      laserBeams: true,
      progressIndicators: true,
      sparkIntensity: 1.0,
    };
    
    this.createMaterials();
    this.createDroneFleet();
    this.createLaserSystem();
    this.createParticleSystem();
    
    this.userData = { 
      id: `fleet-${poolName}`, 
      type: 'construction-fleet',
      poolName: this.poolName 
    };
  }
  
  private generatePoolColor(poolName: string): THREE.Color {
    // Generate consistent color based on pool name
    let hash = 0;
    for (let i = 0; i < poolName.length; i++) {
      hash = poolName.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const hue = Math.abs(hash) % 360;
    const saturation = 0.7 + (Math.abs(hash) % 30) / 100; // 0.7-1.0
    const lightness = 0.5 + (Math.abs(hash) % 20) / 100;  // 0.5-0.7
    
    return new THREE.Color().setHSL(hue / 360, saturation, lightness);
  }
  
  private createMaterials(): void {
    this.droneMaterial = new THREE.MeshLambertMaterial({
      color: this.config.color,
      transparent: true,
      opacity: 0.8,
    });
    
    this.laserMaterial = new THREE.LineBasicMaterial({
      color: this.config.laserColor,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
    });
    
    this.particleMaterial = new THREE.PointsMaterial({
      color: this.config.color,
      size: 0.1,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
  }
  
  private createDroneFleet(): void {
    // Create simple drone geometry (tetrahedron)
    const droneGeometry = new THREE.TetrahedronGeometry(0.2, 0);
    
    this.dronesMesh = new THREE.InstancedMesh(
      droneGeometry,
      this.droneMaterial,
      this.config.maxDrones
    );
    
    // Initialize drone positions in formation
    this.initializeDroneFormation();
    
    this.add(this.dronesMesh);
  }
  
  private initializeDroneFormation(): void {
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const rotation = new THREE.Euler();
    const scale = new THREE.Vector3(1, 1, 1);
    
    for (let i = 0; i < this.config.maxDrones; i++) {
      // Create drone data
      const droneId = `${this.poolName}-drone-${i}`;
      
      // Position drones in a loose formation around base position
      const angle = (i / this.config.maxDrones) * Math.PI * 2;
      const radius = 1 + Math.random() * 2;
      const height = (Math.random() - 0.5) * 2;
      
      position.set(
        this.basePosition.x + Math.cos(angle) * radius,
        this.basePosition.y + height,
        this.basePosition.z + Math.sin(angle) * radius
      );
      
      // Random rotation
      rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      
      matrix.compose(position, new THREE.Quaternion().setFromEuler(rotation), scale);
      this.dronesMesh.setMatrixAt(i, matrix);
      
      // Create drone state
      const drone: FleetDrone = {
        id: droneId,
        position: position.clone(),
        velocity: new THREE.Vector3(),
        target: position.clone(),
        workState: 'idle',
        workProgress: 0,
        lastUpdate: Date.now(),
      };
      
      this.drones.set(droneId, drone);
    }
    
    this.dronesMesh.instanceMatrix.needsUpdate = true;
  }
  
  private createLaserSystem(): void {
    // Create geometry for laser beams (will be updated dynamically)
    const maxLasers = this.config.maxDrones;
    const laserGeometry = new THREE.BufferGeometry();
    
    const positions = new Float32Array(maxLasers * 6); // 2 points per laser, 3 components each
    const colors = new Float32Array(maxLasers * 6);
    
    laserGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    laserGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    this.laserSystem = new THREE.LineSegments(laserGeometry, this.laserMaterial);
    this.add(this.laserSystem);
  }
  
  private createParticleSystem(): void {
    const particleCount = 500;
    const particleGeometry = new THREE.BufferGeometry();
    
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const lifetimes = new Float32Array(particleCount);
    const sizes = new Float32Array(particleCount);
    
    // Initialize particles (they'll be activated during work)
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      
      positions[i3] = 0;
      positions[i3 + 1] = 0;
      positions[i3 + 2] = 0;
      
      velocities[i3] = 0;
      velocities[i3 + 1] = 0;
      velocities[i3 + 2] = 0;
      
      lifetimes[i] = 0;
      sizes[i] = 0.05 + Math.random() * 0.05;
    }
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));
    particleGeometry.setAttribute('lifetime', new THREE.BufferAttribute(lifetimes, 1));
    particleGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    
    this.particleSystem = new THREE.Points(particleGeometry, this.particleMaterial);
    this.add(this.particleSystem);
  }
  
  public assignToSegment(segmentIndex: number, segmentPosition: THREE.Vector3): void {
    this.targetSegment = segmentIndex;
    this.targetPosition.copy(segmentPosition);
    
    // Assign drones to work on this segment
    let droneIndex = 0;
    for (const drone of this.drones.values()) {
      if (droneIndex < this.config.maxDrones) {
        // Set work position around the target segment
        const workOffset = new THREE.Vector3(
          (Math.random() - 0.5) * this.config.workRange,
          (Math.random() - 0.5) * this.config.workRange,
          (Math.random() - 0.5) * this.config.workRange
        );
        
        drone.target.copy(this.targetPosition).add(workOffset);
        drone.workState = 'moving';
        droneIndex++;
      }
    }
    
    this.activeWork = true;
  }
  
  public releaseFromSegment(): void {
    this.targetSegment = -1;
    this.activeWork = false;
    
    // Send all drones back to base
    for (const drone of this.drones.values()) {
      drone.workState = 'returning';
      drone.target.copy(this.basePosition);
    }
  }
  
  public updatePosition(newBasePosition: THREE.Vector3): void {
    this.basePosition.copy(newBasePosition);
    this.position.copy(newBasePosition);
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update drone behaviors
    this.updateDrones(deltaTime);
    
    // Update visual effects
    if (this.workAnimation.laserBeams) {
      this.updateLasers();
    }
    
    if (this.workAnimation.particleEffects) {
      this.updateParticles(deltaTime);
    }
  }
  
  private updateDrones(deltaTime: number): void {
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const rotation = new THREE.Euler();
    const scale = new THREE.Vector3(1, 1, 1);
    
    let droneIndex = 0;
    
    for (const drone of this.drones.values()) {
      if (droneIndex >= this.config.maxDrones) break;
      
      // Update drone state based on current behavior
      switch (drone.workState) {
        case 'idle':
          this.updateIdleDrone(drone, deltaTime);
          break;
        case 'moving':
          this.updateMovingDrone(drone, deltaTime);
          break;
        case 'working':
          this.updateWorkingDrone(drone, deltaTime);
          break;
        case 'returning':
          this.updateReturningDrone(drone, deltaTime);
          break;
      }
      
      // Update visual position
      position.copy(drone.position);
      
      // Rotate based on velocity
      if (drone.velocity.length() > 0.1) {
        const direction = drone.velocity.clone().normalize();
        rotation.setFromRotationMatrix(
          new THREE.Matrix4().lookAt(
            drone.position,
            drone.position.clone().add(direction),
            new THREE.Vector3(0, 1, 0)
          )
        );
      }
      
      matrix.compose(position, new THREE.Quaternion().setFromEuler(rotation), scale);
      this.dronesMesh.setMatrixAt(droneIndex, matrix);
      
      droneIndex++;
    }
    
    this.dronesMesh.instanceMatrix.needsUpdate = true;
  }
  
  private updateIdleDrone(drone: FleetDrone, _deltaTime: number): void {
    // Gentle hovering motion
    const hoverOffset = new THREE.Vector3(
      Math.sin(this.time * 2 + drone.position.x) * 0.1,
      Math.sin(this.time * 1.5 + drone.position.y) * 0.1,
      Math.cos(this.time * 2.2 + drone.position.z) * 0.1
    );
    
    drone.position.copy(this.basePosition).add(hoverOffset);
    drone.velocity.multiplyScalar(0.9); // Damping
  }
  
  private updateMovingDrone(drone: FleetDrone, deltaTime: number): void {
    const direction = drone.target.clone().sub(drone.position);
    const distance = direction.length();
    
    if (distance < 0.5) {
      // Reached target, start working
      drone.workState = 'working';
      drone.workProgress = 0;
    } else {
      // Move toward target
      direction.normalize();
      drone.velocity.lerp(direction.multiplyScalar(this.config.droneSpeed), 0.1);
      drone.position.add(drone.velocity.clone().multiplyScalar(deltaTime));
    }
  }
  
  private updateWorkingDrone(drone: FleetDrone, deltaTime: number): void {
    // Work animation - orbit around work position
    const orbitRadius = 0.5;
    const orbitSpeed = 2.0;
    const angle = this.time * orbitSpeed + drone.position.x;
    
    const orbitOffset = new THREE.Vector3(
      Math.cos(angle) * orbitRadius,
      Math.sin(this.time * 3) * 0.2,
      Math.sin(angle) * orbitRadius
    );
    
    drone.position.copy(drone.target).add(orbitOffset);
    
    // Update work progress
    drone.workProgress += this.config.efficiency * deltaTime;
    
    // Check if work is complete (externally managed)
    if (!this.activeWork) {
      drone.workState = 'returning';
    }
  }
  
  private updateReturningDrone(drone: FleetDrone, deltaTime: number): void {
    const direction = this.basePosition.clone().sub(drone.position);
    const distance = direction.length();
    
    if (distance < 1.0) {
      // Reached base, go idle
      drone.workState = 'idle';
      drone.workProgress = 0;
    } else {
      // Move toward base
      direction.normalize();
      drone.velocity.lerp(direction.multiplyScalar(this.config.droneSpeed), 0.1);
      drone.position.add(drone.velocity.clone().multiplyScalar(deltaTime));
    }
  }
  
  private updateLasers(): void {
    if (!this.workAnimation.laserBeams) return;
    
    const positions = this.laserSystem.geometry.attributes['position'].array as Float32Array;
    const colors = this.laserSystem.geometry.attributes['color'].array as Float32Array;
    
    let laserIndex = 0;
    
    for (const drone of this.drones.values()) {
      if (laserIndex >= this.config.maxDrones || drone.workState !== 'working') {
        // Clear laser
        const i6 = laserIndex * 6;
        positions[i6] = 0; positions[i6 + 1] = 0; positions[i6 + 2] = 0;
        positions[i6 + 3] = 0; positions[i6 + 4] = 0; positions[i6 + 5] = 0;
        colors[i6] = 0; colors[i6 + 1] = 0; colors[i6 + 2] = 0;
        colors[i6 + 3] = 0; colors[i6 + 4] = 0; colors[i6 + 5] = 0;
      } else {
        // Draw laser from drone to target
        const i6 = laserIndex * 6;
        
        // Start point (drone position)
        positions[i6] = drone.position.x;
        positions[i6 + 1] = drone.position.y;
        positions[i6 + 2] = drone.position.z;
        
        // End point (target position)
        positions[i6 + 3] = drone.target.x;
        positions[i6 + 4] = drone.target.y;
        positions[i6 + 5] = drone.target.z;
        
        // Laser color
        const intensity = 0.5 + Math.sin(this.time * 10) * 0.5;
        colors[i6] = this.config.laserColor.r * intensity;
        colors[i6 + 1] = this.config.laserColor.g * intensity;
        colors[i6 + 2] = this.config.laserColor.b * intensity;
        colors[i6 + 3] = this.config.laserColor.r * intensity;
        colors[i6 + 4] = this.config.laserColor.g * intensity;
        colors[i6 + 5] = this.config.laserColor.b * intensity;
      }
      
      laserIndex++;
    }
    
    this.laserSystem.geometry.attributes['position'].needsUpdate = true;
    this.laserSystem.geometry.attributes['color'].needsUpdate = true;
  }
  
  private updateParticles(deltaTime: number): void {
    if (!this.workAnimation.particleEffects || !this.activeWork) return;
    
    const positions = this.particleSystem.geometry.attributes['position'].array as Float32Array;
    const velocities = this.particleSystem.geometry.attributes['velocity'].array as Float32Array;
    const lifetimes = this.particleSystem.geometry.attributes['lifetime'].array as Float32Array;
    
    for (let i = 0; i < lifetimes.length; i++) {
      const i3 = i * 3;
      
      if (lifetimes[i] > 0) {
        // Update existing particle
        positions[i3] += velocities[i3] * deltaTime;
        positions[i3 + 1] += velocities[i3 + 1] * deltaTime;
        positions[i3 + 2] += velocities[i3 + 2] * deltaTime;
        
        lifetimes[i] -= deltaTime;
      } else if (Math.random() < 0.1) {
        // Spawn new particle near work area
        if (this.targetPosition.length() > 0) {
          positions[i3] = this.targetPosition.x + (Math.random() - 0.5) * 2;
          positions[i3 + 1] = this.targetPosition.y + (Math.random() - 0.5) * 2;
          positions[i3 + 2] = this.targetPosition.z + (Math.random() - 0.5) * 2;
          
          velocities[i3] = (Math.random() - 0.5) * 2;
          velocities[i3 + 1] = (Math.random() - 0.5) * 2;
          velocities[i3 + 2] = (Math.random() - 0.5) * 2;
          
          lifetimes[i] = 1 + Math.random() * 2; // 1-3 seconds
        }
      }
    }
    
    this.particleSystem.geometry.attributes['position'].needsUpdate = true;
  }
  
  public getWorkEfficiency(): number {
    // Calculate current work efficiency based on active drones
    let workingDrones = 0;
    for (const drone of this.drones.values()) {
      if (drone.workState === 'working') {
        workingDrones++;
      }
    }
    
    return (workingDrones / this.config.maxDrones) * this.config.efficiency;
  }
  
  public getFleetStatus(): {
    totalDrones: number;
    idleDrones: number;
    workingDrones: number;
    efficiency: number;
    targetSegment: number;
  } {
    let idle = 0, working = 0;
    
    for (const drone of this.drones.values()) {
      if (drone.workState === 'idle') idle++;
      else if (drone.workState === 'working') working++;
    }
    
    return {
      totalDrones: this.config.maxDrones,
      idleDrones: idle,
      workingDrones: working,
      efficiency: this.getWorkEfficiency(),
      targetSegment: this.targetSegment,
    };
  }
  
  public setAnimationSettings(settings: Partial<WorkAnimation>): void {
    this.workAnimation = { ...this.workAnimation, ...settings };
    
    // Update material properties based on settings
    this.laserMaterial.visible = this.workAnimation.laserBeams;
    this.particleMaterial.visible = this.workAnimation.particleEffects;
  }
  
  public dispose(): void {
    // Dispose geometries and materials
    this.dronesMesh.geometry.dispose();
    this.laserSystem.geometry.dispose();
    this.particleSystem.geometry.dispose();
    
    this.droneMaterial.dispose();
    this.laserMaterial.dispose();
    this.particleMaterial.dispose();
    
    // Clear data
    this.drones.clear();
  }
}